/**
 * AbapObjectsTab — SAP ECC ABAP custom-object inventory (Iter 28).
 *
 * Wires up two endpoints:
 *   GET /api/workspaces/{id}/sap-ecc/abap-objects   (filterable list)
 *   GET /api/workspaces/{id}/sap-ecc/abap-summary   (per-type/namespace/package rollup)
 *
 * Layout: summary card strip (totals + by-type chips) + per-namespace + per-package
 * panels above a filterable, paginated table.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CircleNotchIcon as Spinner,
  CodeIcon as Code,
  WarningOctagonIcon as WarningOctagon,
  PackageIcon as Package,
  UsersIcon as Users,
  ChartBarIcon as ChartBar,
  CalendarIcon as Calendar,
  FireIcon as Fire,
  ListIcon as List,
  PuzzlePieceIcon as PuzzlePiece,
} from "@phosphor-icons/react";
import { getAbapSummary, listAbapObjects } from "@/lib/api";
import AbapHeatmapPanel from "./AbapHeatmapPanel";
import AbapEnhancementsPanel from "./AbapEnhancementsPanel";

const PAGE = 50;

// Per-namespace badge colour for the chip rendering. Customer namespaces share
// one neutral tint; SAP-delivered objects get a different visual treatment so
// they're distinguishable at a glance.
const NS_COLOR = {
  Z: { bg: "#fff8e1", fg: "#8a6c00", border: "#f1c21b" },
  Y: { bg: "#edf5ff", fg: "#0043ce", border: "#a6c8ff" },
};

function NamespaceBadge({ ns }) {
  if (!ns) {
    return (
      <span
        className="px-1.5 py-0.5 text-[10px] uppercase tracking-widest border border-[var(--ml-border)] text-[var(--ml-text-secondary)] ml-mono"
        data-testid="abap-ns-sap"
      >
        SAP
      </span>
    );
  }
  const style = NS_COLOR[ns] || { bg: "#f4f4f4", fg: "#525252", border: "#c6c6c6" };
  return (
    <span
      className="px-1.5 py-0.5 text-[10px] uppercase tracking-widest ml-mono border"
      style={{ background: style.bg, color: style.fg, borderColor: style.border }}
      data-testid={`abap-ns-${ns.replace(/\//g, "")}`}
    >
      {ns}
    </span>
  );
}

function CustomFlag({ row }) {
  if (row.is_modified_standard) {
    return (
      <span
        className="px-1.5 py-0.5 text-[10px] uppercase tracking-widest ml-mono inline-flex items-center gap-1"
        style={{ background: "#fff1f1", color: "#a2191f", borderColor: "#ffd7d9", border: "1px solid #ffd7d9" }}
        data-testid="abap-flag-modified"
        title="Standard SAP object that has been modified in place — highest transition risk."
      >
        <WarningOctagon size={10} weight="bold" /> MOD
      </span>
    );
  }
  if (row.is_custom) {
    return (
      <span
        className="px-1.5 py-0.5 text-[10px] uppercase tracking-widest ml-mono"
        style={{ background: "#defbe6", color: "#0e6027", borderColor: "#a7f0ba", border: "1px solid #a7f0ba" }}
        data-testid="abap-flag-custom"
      >
        Z/Y
      </span>
    );
  }
  return (
    <span
      className="px-1.5 py-0.5 text-[10px] uppercase tracking-widest ml-mono text-[var(--ml-text-secondary)] border border-[var(--ml-border)]"
      data-testid="abap-flag-standard"
    >
      STD
    </span>
  );
}

function StatCard({ label, value, color, testId }) {
  return (
    <div className="ml-card p-4" data-testid={testId}>
      <div className="ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{label}</div>
      <div className="text-2xl font-semibold mt-1" style={{ color }}>
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

function SummaryBar({ summary }) {
  if (!summary) return null;
  const { totals, by_type, by_namespace, by_package, by_changed_by } = summary;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="ABAP objects" value={totals.all} color="#0f62fe" testId="abap-stat-all" />
        <StatCard label="Custom (Z/Y)" value={totals.custom} color="#0e6027" testId="abap-stat-custom" />
        <StatCard label="Modified standard" value={totals.modified_standard} color="#a2191f" testId="abap-stat-modified" />
        <StatCard label="Total LOC" value={totals.loc} color="#6929c4" testId="abap-stat-loc" />
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {/* by-type chips */}
        <div className="ml-card p-4" data-testid="abap-by-type">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
            <ChartBar size={11} weight="bold" /> By object type
          </div>
          <div className="flex flex-wrap gap-1.5">
            {by_type.length === 0 && (
              <span className="text-xs text-[var(--ml-text-secondary)]">No data</span>
            )}
            {by_type.map((row) => (
              <span
                key={row.obj_type}
                data-testid={`abap-type-chip-${row.obj_type}`}
                className="ml-mono text-[11px] px-2 py-1 border border-[var(--ml-border)]"
                title={row.loc > 0 ? `${row.loc.toLocaleString()} LOC` : undefined}
              >
                {row.obj_type} <span className="text-[var(--ml-text-secondary)]">· {row.count}</span>
              </span>
            ))}
          </div>
        </div>

        {/* by-namespace chips */}
        <div className="ml-card p-4" data-testid="abap-by-namespace">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
            <Code size={11} weight="bold" /> Custom namespaces
          </div>
          <div className="flex flex-wrap gap-1.5">
            {by_namespace.length === 0 && (
              <span className="text-xs text-[var(--ml-text-secondary)]">No custom code yet</span>
            )}
            {by_namespace.map((row) => (
              <span
                key={row.namespace}
                data-testid={`abap-ns-chip-${row.namespace.replace(/\//g, "")}`}
                className="ml-mono text-[11px] px-2 py-1 border border-[var(--ml-border)]"
              >
                {row.namespace} <span className="text-[var(--ml-text-secondary)]">· {row.count}</span>
              </span>
            ))}
          </div>
        </div>

        {/* by-package */}
        <div className="ml-card p-4" data-testid="abap-by-package">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
            <Package size={11} weight="bold" /> Top packages
          </div>
          <div className="space-y-1">
            {by_package.length === 0 && (
              <span className="text-xs text-[var(--ml-text-secondary)]">No package data</span>
            )}
            {by_package.slice(0, 6).map((row) => (
              <div
                key={row.package}
                className="flex justify-between text-xs"
                data-testid={`abap-pkg-row-${row.package}`}
              >
                <span className="ml-mono">{row.package}</span>
                <span className="ml-mono text-[var(--ml-text-secondary)]">
                  {row.count} {row.loc > 0 ? `· ${row.loc.toLocaleString()} LOC` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {by_changed_by.length > 0 && (
        <div className="ml-card p-4" data-testid="abap-by-author">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2 flex items-center gap-1.5">
            <Users size={11} weight="bold" /> Top changed-by (REPOSRC)
          </div>
          <div className="flex flex-wrap gap-3">
            {by_changed_by.map((row) => (
              <span
                key={row.user}
                className="ml-mono text-xs flex items-center gap-1"
                data-testid={`abap-author-${row.user}`}
              >
                <strong>{row.user}</strong>
                <span className="text-[var(--ml-text-secondary)]">· {row.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterBar({ filters, setFilters, byType, byPackage, byNamespace }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="abap-filter-bar">
      <input
        type="text"
        placeholder="Search name, author, last-changed-by…"
        value={filters.q}
        onChange={(e) => setFilters({ ...filters, q: e.target.value, offset: 0 })}
        data-testid="abap-filter-q"
        className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)]"
      />
      <select
        value={filters.objType}
        onChange={(e) => setFilters({ ...filters, objType: e.target.value, offset: 0 })}
        data-testid="abap-filter-type"
        className="px-2 py-2 text-sm border border-[var(--ml-border)] ml-mono bg-white"
      >
        <option value="">All types</option>
        {byType.map((t) => (
          <option key={t.obj_type} value={t.obj_type}>{t.obj_type} ({t.count})</option>
        ))}
      </select>
      <select
        value={filters.pkg}
        onChange={(e) => setFilters({ ...filters, pkg: e.target.value, offset: 0 })}
        data-testid="abap-filter-package"
        className="px-2 py-2 text-sm border border-[var(--ml-border)] ml-mono bg-white"
      >
        <option value="">All packages</option>
        {byPackage.map((p) => (
          <option key={p.package} value={p.package}>{p.package} ({p.count})</option>
        ))}
      </select>
      <select
        value={filters.namespace}
        onChange={(e) => setFilters({ ...filters, namespace: e.target.value, offset: 0 })}
        data-testid="abap-filter-namespace"
        className="px-2 py-2 text-sm border border-[var(--ml-border)] ml-mono bg-white"
      >
        <option value="">All namespaces</option>
        {byNamespace.map((n) => (
          <option key={n.namespace} value={n.namespace}>{n.namespace} ({n.count})</option>
        ))}
      </select>
      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={filters.customOnly}
          onChange={(e) => setFilters({ ...filters, customOnly: e.target.checked, offset: 0 })}
          data-testid="abap-filter-custom-only"
        />
        Custom only
      </label>
      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={filters.modifiedOnly}
          onChange={(e) => setFilters({ ...filters, modifiedOnly: e.target.checked, offset: 0 })}
          data-testid="abap-filter-modified-only"
        />
        Modified standard
      </label>
    </div>
  );
}

export default function AbapObjectsTab({ workspaceId }) {
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState({ total: 0, rows: [] });
  const [loading, setLoading] = useState(true);
  // Sub-tab toggle for iter 29 — 'inventory' (the iter-28 list) vs 'heatmap'
  // (the iter-29 per-package transition view + AI deliverable).
  const [subTab, setSubTab] = useState("inventory");
  const [filters, setFilters] = useState({
    q: "",
    objType: "",
    pkg: "",
    namespace: "",
    customOnly: false,
    modifiedOnly: false,
    offset: 0,
  });

  // Wraps the listing fetch in a stable callback so we can re-invoke it on
  // filter changes without an effect dep blow-up.
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listAbapObjects(workspaceId, {
        limit: PAGE,
        offset: filters.offset,
        q: filters.q || undefined,
        objType: filters.objType || undefined,
        pkg: filters.pkg || undefined,
        namespace: filters.namespace || undefined,
        customOnly: filters.customOnly,
        modifiedOnly: filters.modifiedOnly,
      });
      setData(list);
    } finally {
      setLoading(false);
    }
  }, [
    workspaceId,
    filters.offset,
    filters.q,
    filters.objType,
    filters.pkg,
    filters.namespace,
    filters.customOnly,
    filters.modifiedOnly,
  ]);

  useEffect(() => {
    // Summary lives independently of filters — only re-fetch on workspace change.
    getAbapSummary(workspaceId).then(setSummary).catch(() => setSummary(null));
  }, [workspaceId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const pageInfo = useMemo(() => {
    const start = filters.offset + 1;
    const end = Math.min(filters.offset + data.rows.length, data.total);
    return { start: data.rows.length ? start : 0, end, total: data.total };
  }, [filters.offset, data.rows.length, data.total]);

  const isEmpty = !loading && summary?.totals?.all === 0;
  if (isEmpty && subTab !== "enhancements") {
    return (
      <div className="space-y-4" data-testid="abap-tab">
        <div className="flex gap-1 border-b border-[var(--ml-border)] -mb-px" data-testid="abap-subtab-bar">
          <button
            type="button"
            onClick={() => setSubTab("inventory")}
            data-testid="abap-subtab-inventory"
            className={`px-3 py-2 ml-mono text-[11px] uppercase tracking-widest flex items-center gap-1.5 border-b-2 ${subTab === "inventory" ? "border-[var(--ml-primary)] text-[var(--ml-text)]" : "border-transparent text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"}`}
          >
            <List size={11} weight="bold" /> Inventory
          </button>
          <button
            type="button"
            disabled
            data-testid="abap-subtab-heatmap"
            className="px-3 py-2 ml-mono text-[11px] uppercase tracking-widest flex items-center gap-1.5 border-b-2 border-transparent text-[var(--ml-text-secondary)] opacity-50"
            title="Import a TADIR extract first"
          >
            <Fire size={11} weight="bold" /> Heat map
          </button>
          <button
            type="button"
            onClick={() => setSubTab("enhancements")}
            data-testid="abap-subtab-enhancements"
            className="px-3 py-2 ml-mono text-[11px] uppercase tracking-widest flex items-center gap-1.5 border-b-2 border-transparent text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"
          >
            <PuzzlePiece size={11} weight="bold" /> Enhancements
          </button>
        </div>
        <div className="ml-card p-10 text-center" data-testid="abap-empty">
          <Code size={40} weight="duotone" className="text-[var(--ml-text-secondary)] inline mb-3" />
          <h3 className="text-lg font-semibold mb-1">No ABAP inventory yet</h3>
          <p className="text-sm text-[var(--ml-text-secondary)] max-w-md mx-auto">
            Drop a <strong>TADIR</strong> export (and optionally <strong>REPOSRC</strong>)
            into the Import tab to populate the custom-code inventory. Templates available there.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="abap-tab">
      <div className="flex gap-1 border-b border-[var(--ml-border)] -mb-px" data-testid="abap-subtab-bar">
        <button
          type="button"
          onClick={() => setSubTab("inventory")}
          data-testid="abap-subtab-inventory"
          className={`px-3 py-2 ml-mono text-[11px] uppercase tracking-widest flex items-center gap-1.5 border-b-2 ${subTab === "inventory" ? "border-[var(--ml-primary)] text-[var(--ml-text)]" : "border-transparent text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"}`}
        >
          <List size={11} weight="bold" /> Inventory
        </button>
        <button
          type="button"
          onClick={() => setSubTab("heatmap")}
          data-testid="abap-subtab-heatmap"
          className={`px-3 py-2 ml-mono text-[11px] uppercase tracking-widest flex items-center gap-1.5 border-b-2 ${subTab === "heatmap" ? "border-[var(--ml-primary)] text-[var(--ml-text)]" : "border-transparent text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"}`}
        >
          <Fire size={11} weight="bold" /> Heat map
        </button>
        <button
          type="button"
          onClick={() => setSubTab("enhancements")}
          data-testid="abap-subtab-enhancements"
          className={`px-3 py-2 ml-mono text-[11px] uppercase tracking-widest flex items-center gap-1.5 border-b-2 ${subTab === "enhancements" ? "border-[var(--ml-primary)] text-[var(--ml-text)]" : "border-transparent text-[var(--ml-text-secondary)] hover:text-[var(--ml-text)]"}`}
        >
          <PuzzlePiece size={11} weight="bold" /> Enhancements
        </button>
      </div>

      {subTab === "heatmap" ? (
        <AbapHeatmapPanel workspaceId={workspaceId} />
      ) : subTab === "enhancements" ? (
        <AbapEnhancementsPanel workspaceId={workspaceId} />
      ) : (
        <>
          <SummaryBar summary={summary} />

          <FilterBar
            filters={filters}
            setFilters={setFilters}
            byType={summary?.by_type || []}
            byPackage={summary?.by_package || []}
            byNamespace={summary?.by_namespace || []}
          />

          {loading && (
            <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="abap-loading">
              <Spinner size={14} className="animate-spin inline mr-2" />Loading ABAP objects…
            </div>
          )}

          {!loading && (
            <>
              <div className="flex items-center justify-between">
                <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="abap-pageinfo">
                  Showing {pageInfo.start}–{pageInfo.end} of {pageInfo.total}
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={filters.offset === 0}
                    onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, f.offset - PAGE) }))}
                    data-testid="abap-prev-page"
                    className="px-3 py-1.5 text-xs border border-[var(--ml-border)] disabled:opacity-40 hover:bg-[var(--ml-surface)]/40"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    disabled={filters.offset + PAGE >= data.total}
                    onClick={() => setFilters((f) => ({ ...f, offset: f.offset + PAGE }))}
                    data-testid="abap-next-page"
                    className="px-3 py-1.5 text-xs border border-[var(--ml-border)] disabled:opacity-40 hover:bg-[var(--ml-surface)]/40"
                  >
                    Next
                  </button>
                </div>
              </div>

              {data.rows.length === 0 ? (
                <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="abap-no-results">
                  No ABAP objects match the current filters.
                </div>
              ) : (
                <div className="ml-card overflow-x-auto" data-testid="abap-objects-table">
                  <table className="w-full text-sm">
                    <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
                      <tr>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Object name</th>
                        <th className="px-3 py-2 text-left">NS</th>
                        <th className="px-3 py-2 text-left">Flag</th>
                        <th className="px-3 py-2 text-left">Package</th>
                        <th className="px-3 py-2 text-left">SrcSys</th>
                        <th className="px-3 py-2 text-left">Author</th>
                        <th className="px-3 py-2 text-left">Last changed</th>
                        <th className="px-3 py-2 text-right">LOC</th>
                        <th className="px-3 py-2 text-left">Transport</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r) => (
                        <tr
                          key={r.id}
                          data-testid={`abap-row-${r.obj_name}`}
                          className="border-b border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/30"
                        >
                          <td className="px-3 py-2 ml-mono font-semibold">{r.obj_type}</td>
                          <td className="px-3 py-2 ml-mono">{r.obj_name}</td>
                          <td className="px-3 py-2"><NamespaceBadge ns={r.namespace} /></td>
                          <td className="px-3 py-2"><CustomFlag row={r} /></td>
                          <td className="px-3 py-2 ml-mono">{r.package || "—"}</td>
                          <td className="px-3 py-2 ml-mono">{r.source_system || "—"}</td>
                          <td className="px-3 py-2 ml-mono text-xs">{r.author || "—"}</td>
                          <td className="px-3 py-2 ml-mono text-xs">
                            {r.last_changed_at ? (
                              <span className="inline-flex items-center gap-1">
                                <Calendar size={10} weight="bold" />
                                {r.last_changed_at.slice(0, 10)}
                                {r.last_changed_by && <span className="text-[var(--ml-text-secondary)]"> · {r.last_changed_by}</span>}
                              </span>
                            ) : (
                              r.last_changed_by || "—"
                            )}
                          </td>
                          <td className="px-3 py-2 ml-mono text-right">{r.loc != null ? r.loc.toLocaleString() : "—"}</td>
                          <td className="px-3 py-2 ml-mono text-xs">{r.transport_id || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
